import { Link } from "react-router-dom";
import { useState } from "react";
import { ArrowUpRight, Mail, MapPin, Phone, Linkedin, Instagram } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import T from "./EditableText";

export default function Footer() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);

    const submitNewsletter = async (e) => {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        try {
            await api.post("/leads", { email, source: "footer_newsletter" });
            toast.success("Danke! Wir melden uns bald.");
            setEmail("");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Fehler.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <footer className="bg-aimploy-dark text-white relative overflow-hidden" data-testid="main-footer">
            <div className="absolute inset-0 bg-dots-dark opacity-50" />
            <div className="absolute -top-20 -right-20 h-96 w-96 rounded-full bg-aimploy-violet/20 blur-3xl" />

            <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 pt-24 pb-12">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-20">
                    <div className="lg:col-span-5">
                        <Link to="/" className="inline-flex items-center gap-2 mb-6">
                            <div className="h-10 w-10 rounded-lg bg-white flex items-center justify-center">
                                <span className="font-display font-bold text-aimploy-navy text-lg">a.</span>
                            </div>
                            <span className="font-display font-bold text-2xl tracking-tight">aimploy</span>
                        </Link>
                        <p className="font-display text-3xl lg:text-4xl font-bold leading-tight max-w-md mb-8">
                            <T k="footer.headline.line1">Wir besetzen die Rollen,</T>
                            <br />
                            <T k="footer.headline.line2">die Ihre Zukunft</T>{" "}
                            <T k="footer.headline.accent" className="text-aimploy-violet">definieren</T>.
                        </p>
                        <form onSubmit={submitNewsletter} className="flex gap-2 max-w-md" data-testid="footer-newsletter-form">
                            <input
                                type="email"
                                placeholder="Ihre E-Mail-Adresse"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                data-testid="footer-newsletter-email"
                                className="flex-1 bg-white/5 border border-white/15 rounded-full px-5 py-3 text-sm placeholder-white/40 focus:bg-white/10"
                            />
                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="footer-newsletter-submit"
                                className="bg-aimploy-violet hover:bg-aimploy-violet-hover text-white px-5 py-3 rounded-full text-sm font-semibold disabled:opacity-60"
                            >
                                {loading ? "..." : <ArrowUpRight className="w-4 h-4" />}
                            </button>
                        </form>
                    </div>

                    <div className="lg:col-span-2">
                        <T k="footer.col1.heading" as="h4" className="text-xs uppercase tracking-[0.2em] text-white/40 font-semibold mb-5">Services</T>
                        <ul className="space-y-3 text-sm text-white/80">
                            <li><Link to="/unternehmen" className="hover:text-aimploy-violet"><T k="footer.col1.link0" as="span">Executive Search</T></Link></li>
                            <li><Link to="/unternehmen" className="hover:text-aimploy-violet"><T k="footer.col1.link1" as="span">Tech & Engineering</T></Link></li>
                            <li><Link to="/unternehmen" className="hover:text-aimploy-violet"><T k="footer.col1.link2" as="span">Sales & Growth</T></Link></li>
                            <li><Link to="/unternehmen" className="hover:text-aimploy-violet"><T k="footer.col1.link3" as="span">Retained Search</T></Link></li>
                        </ul>
                    </div>
                    <div className="lg:col-span-2">
                        <T k="footer.col2.heading" as="h4" className="text-xs uppercase tracking-[0.2em] text-white/40 font-semibold mb-5">Unternehmen</T>
                        <ul className="space-y-3 text-sm text-white/80">
                            <li><Link to="/ueber-uns" className="hover:text-aimploy-violet"><T k="footer.col2.link0" as="span">Über uns</T></Link></li>
                            <li><Link to="/kandidaten" className="hover:text-aimploy-violet"><T k="footer.col2.link1" as="span">Karriere</T></Link></li>
                            <li><Link to="/kontakt" className="hover:text-aimploy-violet"><T k="footer.col2.link2" as="span">Kontakt</T></Link></li>
                            <li><Link to="/admin/login" className="hover:text-aimploy-violet"><T k="footer.col2.link3" as="span">Admin</T></Link></li>
                        </ul>
                    </div>
                    <div className="lg:col-span-3">
                        <T k="footer.col3.heading" as="h4" className="text-xs uppercase tracking-[0.2em] text-white/40 font-semibold mb-5">Kontakt</T>
                        <ul className="space-y-3 text-sm text-white/80">
                            <li className="flex items-start gap-3"><MapPin className="w-4 h-4 mt-0.5 text-aimploy-violet flex-shrink-0" /> <T k="footer.address" as="span">Leopoldstraße 2–8, 32051 Herford</T></li>
                            <li className="flex items-start gap-3"><Mail className="w-4 h-4 mt-0.5 text-aimploy-violet flex-shrink-0" /> <T k="footer.email" as="span">kontakt@aimploy.de</T></li>
                            <li className="flex items-start gap-3"><Phone className="w-4 h-4 mt-0.5 text-aimploy-violet flex-shrink-0" /> <T k="footer.phone" as="span">+49 175 3669882</T></li>
                        </ul>
                        <div className="flex gap-3 mt-6">
                            <a href="https://www.linkedin.com/company/aimploy-ug" target="_blank" rel="noopener noreferrer" data-testid="footer-linkedin" className="h-10 w-10 rounded-full bg-white/5 hover:bg-aimploy-violet flex items-center justify-center transition-colors" aria-label="LinkedIn"><Linkedin className="w-4 h-4" /></a>
                            <a href="https://www.instagram.com/aimploy.ug/" target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" className="h-10 w-10 rounded-full bg-white/5 hover:bg-aimploy-violet flex items-center justify-center transition-colors" aria-label="Instagram"><Instagram className="w-4 h-4" /></a>
                        </div>
                    </div>
                </div>

                <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 text-xs text-white/40">
                    <div>© {new Date().getFullYear()} Aimploy UG (haftungsbeschränkt). Alle Rechte vorbehalten.</div>
                    <div className="flex gap-6">
                        <Link to="/impressum" data-testid="footer-impressum" className="hover:text-white">Impressum</Link>
                        <Link to="/datenschutz" data-testid="footer-datenschutz" className="hover:text-white">Datenschutz</Link>
                        <Link to="/agb" data-testid="footer-agb" className="hover:text-white">AGB</Link>
                    </div>
                </div>
            </div>
        </footer>
    );
}
